import { useEffect, useRef, useState } from "react";
import ComparisonDrawer from "./components/ComparisonDrawer";
import BrowseNavigator from "./components/BrowseNavigator";
import DocumentReader from "./components/DocumentReader";
import ResultList from "./components/ResultList";
import SearchHeader from "./components/SearchHeader";
import UpdateCenter from "./components/UpdateCenter";
import WorkProgress from "./components/WorkProgress";
import {
  loadDocument,
  loadLibraryCatalog,
  loadExpressions,
  loadSourcePreview,
  searchDocuments,
} from "./lib/api";
import { useLocale } from "./lib/LocaleProvider";
import { readReaderUrlState, writeReaderUrlState } from "./lib/readerUrlState";
import { resolveNavigationTarget } from "./lib/legalSections";
import {
  preferredExpressionId as choosePreferredExpressionId,
  selectExpressionId,
  selectParallelExpressionIds,
} from "./lib/expressionSelection";

export default function App() {
  const initialUrlState = useRef(readReaderUrlState());
  const { locale, text } = useLocale();
  const [query, setQuery] = useState(initialUrlState.current.query);
  const [language, setLanguage] = useState(
    initialUrlState.current.searchLanguage || (locale === "en" ? "en" : ""),
  );
  const [documentType, setDocumentType] = useState(initialUrlState.current.documentType);
  const [year, setYear] = useState(initialUrlState.current.year);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [expressions, setExpressions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastSearchedQuery, setLastSearchedQuery] = useState("");
  const [searchRequest, setSearchRequest] = useState(0);
  const [readerBusy, setReaderBusy] = useState(false);
  const [errorKey, setError] = useState("");
  const error = errorKey ? text(errorKey) : "";
  const [mobileReader, setMobileReader] = useState(false);
  const [comparisonItems, setComparisonItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sourcePreview, setSourcePreview] = useState(null);
  const [sourceBusy, setSourceBusy] = useState(false);
  const [readerMode, setReaderMode] = useState(
    initialUrlState.current.readerMode || (locale === "en" ? "en" : "th"),
  );
  const [parallelBundles, setParallelBundles] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [navigationMode, setNavigationMode] = useState(
    initialUrlState.current.navigationMode === "search" || initialUrlState.current.query
      ? "search"
      : "browse",
  );
  const [catalog, setCatalog] = useState(null);
  const [catalogBusy, setCatalogBusy] = useState(true);
  const [catalogRetry, setCatalogRetry] = useState(0);
  const [catalogErrorKey, setCatalogError] = useState("");
  const catalogError = catalogErrorKey ? text(catalogErrorKey) : "";
  const [readerTargetSequence, setReaderTargetSequence] = useState(null);
  const [urlHydrated, setUrlHydrated] = useState(false);
  const availableYears = [
    ...new Set(
      (catalog?.document_types || []).flatMap((item) =>
        item.years.map((yearItem) => yearItem.year_be).filter(Boolean),
      ),
    ),
  ].sort((left, right) => right - left);

  function preferredExpressionId(expressionsForWork, requestedId = "", languageOverride = "") {
    const requestedLanguage = languageOverride || (readerMode === "parallel"
      ? locale
      : readerMode === "en" || readerMode === "th"
        ? readerMode
        : locale);
    return choosePreferredExpressionId(expressionsForWork, requestedId, requestedLanguage, locale);
  }

  async function runSearch(event, switchMode = true) {
    event?.preventDefault();
    if (!query.trim()) return false;
    setLastSearchedQuery(query.trim());
    if (switchMode) setNavigationMode("search");
    setBusy(true);
    setError("");
    try {
      const payload = await searchDocuments({
        query,
        language,
        documentType,
        year,
      });
      setResults(payload.results);
      if (payload.results.length) {
        await selectResult(payload.results[0], false);
      } else {
        setSelected(null);
        setBundle(null);
      }
      if (switchMode) setMobileReader(false);
      return true;
    } catch {
      setError("results.searchError");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function selectResult(result, openMobile = true, targetSequence = null) {
    setReaderTargetSequence(null);
    setReaderBusy(true);
    if (openMobile) setMobileReader(true);
    try {
      const initialBundle = await loadDocument(result.expression_id);
      let companions = { expressions: [initialBundle.expression] };
      try {
        companions = await loadExpressions(result.work_id);
      } catch {
        // A document can still be read when its companion-edition list is unavailable.
      }
      const availableExpressions = [...(companions.expressions || [])];
      if (!availableExpressions.some((item) => item.expression_id === result.expression_id)) {
        availableExpressions.push(initialBundle.expression);
      }
      const preferredId = preferredExpressionId(
        availableExpressions,
        result.expression_id,
      );
      const nextBundle = preferredId && preferredId !== result.expression_id
        ? await loadDocument(preferredId)
        : initialBundle;
      let selectedBundle = nextBundle;
      let selectedParallelBundles = null;
      let selectedReaderMode = nextBundle.expression.language === "en" ? "en" : "th";
      if (readerMode === "parallel") {
        const { th: thaiId, en: englishId } = selectParallelExpressionIds(
          availableExpressions,
          result.expression_id,
        );
        if (thaiId && englishId) {
          const [thai, english] = await Promise.all([
            loadDocument(thaiId),
            loadDocument(englishId),
          ]);
          selectedBundle = thai;
          selectedParallelBundles = { th: thai, en: english };
          selectedReaderMode = "parallel";
        }
      }
      const selectedExpressionId = selectedBundle.expression.expression_id
        || preferredId
        || result.expression_id;
      const mappedSequence = resolveNavigationTarget(initialBundle, selectedBundle, targetSequence);
      const target = mappedSequence == null ? null : selectedBundle.segments?.find(
        segment => String(segment.sequence) === String(mappedSequence),
      );
      const targetProvisionId = target?.provision_id || "";
      setReaderTargetSequence(target?.sequence ?? null);
      writeReaderUrlState({
        navigationMode,
        workId: result.work_id,
        expressionId: selectedExpressionId,
        provisionId: targetProvisionId,
      }, { push: selected?.expression_id !== selectedExpressionId });
      setSelected({ ...result, expression_id: selectedExpressionId, language: selectedBundle.expression.language });
      setBundle(selectedBundle);
      setReaderMode(selectedReaderMode);
      setParallelBundles(selectedParallelBundles);
      setExpressions(availableExpressions.length ? availableExpressions : [selectedBundle.expression]);
    } catch {
      setBundle(null);
      setExpressions([]);
      setError("reader.openError");
    } finally {
      setReaderBusy(false);
    }
  }

  async function changeExpression(expressionId) {
    if (!expressionId || expressionId === bundle?.expression.expression_id) return;
    setReaderBusy(true);
    try {
      setBundle(await loadDocument(expressionId));
    } finally {
      setReaderBusy(false);
    }
  }

  async function changeReaderMode(mode) {
    if (mode === readerMode) return;
    if (mode === "parallel") {
      const { th: thaiId, en: englishId } = selectParallelExpressionIds(
        expressions,
        bundle?.expression.language === "th" || bundle?.expression.language === "en"
          ? bundle.expression.expression_id
          : "",
      );
      if (!thaiId || !englishId) return;
      setReaderBusy(true);
      try {
        const [thai, english] = await Promise.all([
          loadDocument(thaiId),
          loadDocument(englishId),
        ]);
        setBundle(thai);
        setParallelBundles({ th: thai, en: english });
        setReaderMode("parallel");
      } finally {
        setReaderBusy(false);
      }
      return;
    }
    const currentId = bundle?.expression.language === mode
      ? bundle.expression.expression_id
      : "";
    const expressionId = preferredExpressionId(expressions, currentId, mode);
    if (!expressionId) return;
    setReaderBusy(true);
    try {
      setBundle(await loadDocument(expressionId));
      setReaderMode(mode);
    } finally {
      setReaderBusy(false);
    }
  }

  async function openCitation(
    expressionId,
    workId,
    focusQuery,
    targetSequence,
    previewOnly = false,
    action = "compare",
  ) {
    if (!expressionId || !workId) return null;
    try {
      let nextBundle = await loadDocument(expressionId);
      if (!previewOnly && action === "open") {
        let companions = { expressions: [nextBundle.expression] };
        try {
          companions = await loadExpressions(workId);
        } catch {
          // Keep the cited edition open even when companion metadata is unavailable.
        }
        const preferredId = preferredExpressionId(companions.expressions || [], expressionId);
        if (preferredId && preferredId !== expressionId) nextBundle = await loadDocument(preferredId);
        setSelected({
          expression_id: nextBundle.expression.expression_id || expressionId,
          work_id: workId,
          title:
            nextBundle.document.display_title_th ||
            nextBundle.document.title_th,
        });
        setBundle(nextBundle);
        setExpressions(companions.expressions || [nextBundle.expression]);
        setReaderMode(nextBundle.expression.language === "en" ? "en" : "th");
        setReaderTargetSequence(targetSequence);
        setMobileReader(true);
        writeReaderUrlState({
          workId,
          expressionId: nextBundle.expression.expression_id || expressionId,
          provisionId: "",
        }, { push: true });
      } else if (!previewOnly) {
        addComparisonBundle(nextBundle, focusQuery, targetSequence);
      }
      return nextBundle;
    } catch {
      setError("reader.citationError");
      return null;
    }
  }

  async function addComparisonResult(result) {
    if (!result?.expression_id) return;
    try {
      const companions = await loadExpressions(result.work_id);
      const expressionId = preferredExpressionId(companions.expressions || [], result.expression_id);
      const nextBundle = await loadDocument(expressionId || result.expression_id);
      addComparisonBundle(nextBundle, query);
    } catch {
      setError("reader.citationError");
    }
  }

  async function searchComparisonDocuments(nextQuery) {
    if (!nextQuery?.trim()) return;
    setBusy(true);
    try {
      const payload = await searchDocuments({ query: nextQuery, language: "", documentType: "", year: "" });
      setResults(payload.results || []);
    } catch {
      setError("results.searchError");
    } finally {
      setBusy(false);
    }
  }

  function addComparisonBundle(nextBundle, focusQuery = "", targetSequence = null) {
    const expressionId = nextBundle.expression.expression_id;
    setComparisonItems((current) => {
      if (current.some((item) => item.expressionId === expressionId)) {
        return current;
      }
      if (current.length >= 4) {
        setError("comparison.limitError");
        return current;
      }
      const next = [...current, { expressionId, bundle: nextBundle, focusQuery, targetSequence }];
      if (next.length >= 1) setDrawerOpen(true);
      return next;
    });
  }

  function compareCurrent() {
    if (bundle) addComparisonBundle(bundle, query);
  }

  async function openSource(source) {
    if (!source?.sha256) return;
    setSourceBusy(true);
    try {
      setSourcePreview(await loadSourcePreview(source.sha256));
    } catch {
      setError("source.openError");
    } finally {
      setSourceBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setCatalogBusy(true);
    setCatalogError("");
    loadLibraryCatalog({ language: locale })
      .then(value => { if (!cancelled) setCatalog(value); })
      .catch(() => { if (!cancelled) setCatalogError("browse.catalogError"); })
      .finally(() => { if (!cancelled) setCatalogBusy(false); });
    return () => { cancelled = true; };
  }, [locale, catalogRetry]);

  useEffect(() => {
    if (!urlHydrated) return;
    writeReaderUrlState({
      query,
      searchLanguage: language,
      documentType,
      year,
      navigationMode,
      workId: selected?.work_id || "",
      expressionId: selected?.expression_id || "",
      readerMode,
      asOfDate: initialUrlState.current.asOfDate || new Date().toISOString().slice(0, 10),
    });
  }, [query, language, documentType, year, navigationMode, selected, readerMode, locale, urlHydrated]);

  useEffect(() => {
    const initial = initialUrlState.current;
    if (!initial.expressionId && !initial.workId && !initial.query) {
      setUrlHydrated(true);
      return undefined;
    }
    async function restoreReader() {
      try {
        let restoredResults = [];
        if (initial.query) {
          const payload = await searchDocuments({
            query: initial.query,
            language: initial.searchLanguage || (locale === "en" ? "en" : ""),
            documentType: initial.documentType,
            year: initial.year,
          });
          restoredResults = payload.results;
          setResults(restoredResults);
        }
        const workCompanions = initial.workId && !initial.expressionId
          ? await loadExpressions(initial.workId)
          : null;
        const requestedExpressionId = initial.expressionId
          || preferredExpressionId(workCompanions?.expressions || [])
          || restoredResults[0]?.expression_id
          || "";
        if (!requestedExpressionId) {
          setUrlHydrated(true);
          return;
        }
        const result = restoredResults.find(
          (item) => item.expression_id === requestedExpressionId,
        ) || {
          expression_id: requestedExpressionId,
          work_id: initial.workId,
        };
        const initialBundle = await loadDocument(requestedExpressionId);
        const restoredWorkId = initialBundle.expression.work_id || initialBundle.document.work_id || result.work_id;
        const companions = workCompanions || await loadExpressions(restoredWorkId);
        const availableExpressions = [...(companions.expressions || [])];
        if (!availableExpressions.some((item) => item.expression_id === initialBundle.expression.expression_id)) {
          availableExpressions.push(initialBundle.expression);
        }
        const preferredId = initial.expressionId && initial.readerMode
          ? selectExpressionId(
            availableExpressions,
            requestedExpressionId,
            initialBundle.expression.language,
          )
          : preferredExpressionId(availableExpressions, requestedExpressionId);
        const nextBundle = preferredId && preferredId !== requestedExpressionId
          ? await loadDocument(preferredId)
          : initialBundle;
        let restoredBundle = nextBundle;
        let restoredParallelBundles = null;
        let restoredReaderMode = nextBundle.expression.language === "en" ? "en" : "th";
        if (initial.readerMode === "parallel") {
          const { th: thaiId, en: englishId } = selectParallelExpressionIds(
            availableExpressions,
            requestedExpressionId,
          );
          if (thaiId && englishId) {
            const [thai, english] = await Promise.all([
              loadDocument(thaiId),
              loadDocument(englishId),
            ]);
            restoredBundle = thai;
            restoredParallelBundles = { th: thai, en: english };
            restoredReaderMode = "parallel";
          }
        }
        const restoredExpressionId = restoredBundle.expression.expression_id
          || preferredId
          || requestedExpressionId;
        setSelected({
          ...result,
          work_id: restoredWorkId,
          expression_id: restoredExpressionId,
          title: result.title || restoredBundle.document.display_title_th || restoredBundle.document.title_th,
        });
        setBundle(restoredBundle);
        setParallelBundles(restoredParallelBundles);
        setExpressions(availableExpressions);
        setReaderMode(restoredReaderMode);
        setMobileReader(true);
        setUrlHydrated(true);
      } catch {
        setError("reader.openError");
        setUrlHydrated(true);
      }
    }
    restoreReader();
    return undefined;
  }, []);

  useEffect(() => {
    const restoreFromHistory = () => window.location.reload();
    window.addEventListener("popstate", restoreFromHistory);
    return () => window.removeEventListener("popstate", restoreFromHistory);
  }, []);

  const showBrowse = navigationMode === "browse" || results.length === 0;
  const libraryHome = navigationMode === "search" && showBrowse && !bundle && !readerBusy && !drawerOpen;
  function navigate(mode) {
    setNavigationMode(mode);
    setMobileReader(false);
    setFocusMode(false);
    if (mode === "search") setSearchRequest(value => value + 1);
  }

  return (
    <div className={`app-shell${libraryHome ? " library-discovery" : ""}`}>
      <SearchHeader
        query={query}
        setQuery={setQuery}
        language={language}
        setLanguage={setLanguage}
        documentType={documentType}
        setDocumentType={setDocumentType}
        year={year}
        setYear={setYear}
        onSubmit={runSearch}
        busy={busy}
        comparisonCount={comparisonItems.length}
          onOpenComparison={() => setDrawerOpen(true)}
        navigationMode={navigationMode}
        onNavigationModeChange={navigate}
        searchRequest={searchRequest}
        availableYears={availableYears}
      />
      <UpdateCenter />
      <WorkProgress />
      <div
        className={`workspace ${libraryHome ? "library-home" : ""} ${mobileReader ? "show-reader" : ""} ${
          drawerOpen ? "drawer-visible" : ""
        } ${focusMode ? "focus-mode" : ""
        }`}
      >
        {showBrowse ? (
          <BrowseNavigator
            catalog={catalog}
            catalogBusy={catalogBusy}
            catalogError={catalogError}
            selectedId={selected?.expression_id}
            language={locale}
            onCatalogRetry={() => setCatalogRetry(value => value + 1)}
            notice={navigationMode === "search" && !busy ? error || (lastSearchedQuery && lastSearchedQuery === query.trim() ? text("results.empty") : "") : ""}
            onSelect={(result, openMobile, targetSequence) => {
              setNavigationMode("browse");
              return selectResult(result, openMobile, targetSequence);
            }}
          />
        ) : (
          <ResultList
            results={results}
            selectedId={selected?.expression_id}
            onSelect={selectResult}
            query={query}
            error={error}
            year={year}
          />
        )}
        {!libraryHome && <DocumentReader
          bundle={bundle}
          busy={readerBusy}
          mobileReader={mobileReader}
          onBack={() => setMobileReader(false)}
          originMode={navigationMode}
          expressions={expressions}
          onExpressionChange={changeExpression}
          viewMode={readerMode}
          parallelBundles={parallelBundles}
          onViewModeChange={changeReaderMode}
          onCitationOpen={openCitation}
          onCompareCurrent={compareCurrent}
          onSourceOpen={openSource}
          query={query}
          targetSequence={readerTargetSequence}
          focusMode={focusMode}
          onFocusModeChange={setFocusMode}
          sourcePreview={sourcePreview}
          sourceBusy={sourceBusy}
          onSourceClose={() => setSourcePreview(null)}
        />}
        <ComparisonDrawer
          open={drawerOpen}
          items={comparisonItems}
          availableDocuments={results}
          busy={busy}
          onSearch={searchComparisonDocuments}
          onAddDocument={addComparisonResult}
          onClose={() => setDrawerOpen(false)}
          onRemove={(expressionId) =>
            setComparisonItems((current) =>
              current.filter((item) => item.expressionId !== expressionId),
            )
          }
          onClear={() => setComparisonItems([])}
          onCitationOpen={openCitation}
          onSourceOpen={openSource}
        />
      </div>
    </div>
  );
}
